import {Trans} from '@lingui/macro';
import {noop} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBox} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBox';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {AccountModel} from '../../../models/api/accountModel';
import {CrmSyncSourcesEnum} from '../../../models/api/crmSyncModel';
import {
  AccountDetailsSectionsEnum,
  defaultAccountDetailsSectionsExpandState,
} from '../../../models/api/localPreferencesModel';
import {makeAccountDetailsPanelId} from '../../../store/panels/panelStateBase';
import {Accordion} from '../../common/accordion/accordion';
import {AccountDetailsContext} from '../accountDetails/accountDetailsContext';
import {AccountDetailsHeaderContainer} from '../accountDetails/accountDetailsHeaderContainer';
import {buildAccountInformationSection} from '../accountDetails/helpers/accordianSectionHelpers';
import {PreviewDropdown} from './dropdown/previewDropdown';

/*
 * Props.
 */

interface AccountCrmPreviewProps {
  accounts: ReadonlyArray<AccountModel>;
  selectedAccount: AccountModel;
  source: CrmSyncSourcesEnum;
  updateSelectedId: HandlerOf<number>;
}

/*
 * Style.
 */

const StyledAccountCrmPreviewWrapperDiv = styled.div`
  background: ${(p) => p.theme.greys.shade30};
  padding: 16px;
  height: 100%;
  overflow: auto;
`;

const StyledAccountPreviewWrapperDiv = styled.div`
  margin-top: 16px;
  background: ${(p) => p.theme.greys.white};
  border-radius: 8px;
`;

const StyledDropdownTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.small};
  margin-bottom: -6px;
`;

const StyledAccordion = styled(Accordion)`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

/*
 * Component.
 */

export const AccountCrmPreview: FC<AccountCrmPreviewProps> = (props) => {
  const theme = useUserTheme();
  const {accounts, selectedAccount, source, updateSelectedId} = props;
  const [mockPanelId] = useState(makeAccountDetailsPanelId());
  const [expandState, setExpandState] = useState(defaultAccountDetailsSectionsExpandState);

  return (
    <StyledAccountCrmPreviewWrapperDiv>
      <StyledDropdownTitleDiv>
        <Trans id="account-crm-preview-dropdown-title">Pick an account to preview:</Trans>
      </StyledDropdownTitleDiv>
      <PopoverCoordinator
        renderButton={(hasHover) => (
          <DropdownBox
            hasHover={hasHover}
            backgroundColor={theme.alphas.gray10}
            backgroundHoverColor={theme.alphas.gray20}
            value={selectedAccount?.name}
            boxStyles={{marginBottom: '4px'}}
          />
        )}
        renderModal={(popoverProps) => (
          <PreviewDropdown
            popoverProps={popoverProps}
            selectedId={selectedAccount.id}
            resources={accounts}
            resourceName="accounts"
            onSelect={updateSelectedId}
            crmSyncSource={source}
          />
        )}
      />
      {selectedAccount && (
        <AccountDetailsContext.Provider value={{panelId: mockPanelId, onTrackEvent: noop}}>
          <StyledAccountPreviewWrapperDiv>
            <AccountDetailsHeaderContainer account={selectedAccount} shouldDisableActions />
            <StyledAccordion
              key="standard-accordion"
              allowMultiSelect
              sections={[
                buildAccountInformationSection({
                  panelId: mockPanelId,
                  expandState,
                  onAccountDetailsSectionToggled: () =>
                    setExpandState((state) => ({
                      ...state,
                      [AccountDetailsSectionsEnum.ACCOUNT_INFORMATION]:
                        !state[AccountDetailsSectionsEnum.ACCOUNT_INFORMATION],
                    })),
                  unsavedAccount: selectedAccount,
                  updateUnsavedAccount: noop,
                  shouldDisableActions: true,
                }),
              ]}
            />
          </StyledAccountPreviewWrapperDiv>
        </AccountDetailsContext.Provider>
      )}
    </StyledAccountCrmPreviewWrapperDiv>
  );
};
